import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  Users,
  GraduationCap,
  BookOpen,
  TrendingUp,
  Award,
  Search,
  X,
  ChevronDown,
  ChevronLeft,
  RotateCcw,
} from "lucide-react";
import "./adminReport.scss";
import { useNavigate } from "react-router-dom";

import {
  pal,
  hex2rgba,
  toPercent,
  mkOpts,
  ScoreRing,
  StatCard,
  ChartCard,
  NoData,
  PeriodTabs,
  gradeBadge,
} from "../../Reusable_Components/Report";
import { StudentDetailContent } from "./studentDetailedContent";
import { TeacherDetailContent } from "./teacherDetailedContent";
import { useDispatch, useSelector } from "react-redux";
import { getAdminReport } from "../../Redux/Admin_Panel/people/people.action";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function toggleSort(setter, key) {
  setter((prev) =>
    prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: "desc" },
  );
}

function rankByEngagementAndScore(list) {
  if (!list.length) return [];
  const maxAttempts = Math.max(...list.map((s) => s.attempts), 1);
  return list
    .map((s) => ({
      ...s,
      composite: (s.attempts / maxAttempts) * 0.5 + (s.avgPct / 100) * 0.5,
    }))
    .sort((a, b) => b.composite - a.composite); // highest composite first
}

function rankTeachersByComposite(list) {
  if (!list.length) return [];
  const maxAssignments = Math.max(...list.map((t) => t.assignments), 1);
  return list
    .map((t) => ({
      ...t,
      composite:
        (t.assignments / maxAssignments) * (1 / 3) +
        (t.avgScore / 100) * (1 / 3) +
        (t.attendance / 100) * (1 / 3),
    }))
    .sort((a, b) => b.composite - a.composite);
}

const SCORE_BUCKETS = [
  "0–10",
  "10–20",
  "20–30",
  "30–40",
  "40–50",
  "50–60",
  "60–70",
  "70–80",
  "80–90",
  "90–100",
];
const BUCKET_COLORS = [
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#eab308",
  "#84cc16",
  "#22c55e",
  "#10b981",
  "#06b6d4",
  "#3b82f6",
  "#8b5cf6",
];
const BUCKET_COLORS_DIM = BUCKET_COLORS.map((c) => hex2rgba(c, 0.18));

function transformAdminReport(report) {
  const assignments = [];
  let idCounter = 1;
  const teachers = report?.teachers || [];

  teachers.forEach((teacher) => {
    const teacherAssignments = teacher?.assignments || [];
    teacherAssignments.forEach((a) => {
      assignments.push({
        id: idCounter++,
        assignment_id: a.assignment_id,
        teacher_id: teacher.teacher_id,
        teacher_name: teacher.teacher_name,
        subject: a.subject,
        chapter: a.chapter,
        class_name: a.assigned_class,
        division: a.assigned_division,
        assigned_date: a.assigned_date,
        no_of_students_assigned: a.no_of_students_assigned ?? 0,
        no_of_students_attempted: a.no_of_students_attempted ?? 0,
        student_details: (a.student_details || []).map((s) => ({
          student_id: s.student_id,
          student_name: s.name,
          attempted: !!s.attempted,
          marks_obtained: s.marks_obtained,
          max_marks: s.max_marks,
          percentage: s.percentage,
        })),
      });
    });
  });

  return assignments;
}

/* ─────────────────────────────────────────────────────────────────────────
   Presentational building blocks
   ───────────────────────────────────────────────────────────────────────── */
function KpiChip({ icon, label, value, color }) {
  return (
    <div className="adm-kpi-chip">
      <span
        className="adm-kpi-icon"
        style={{ color, background: hex2rgba(color, 0.12) }}
      >
        {icon}
      </span>
      <div>
        <div className="adm-kpi-label">{label}</div>
        <div className="adm-kpi-value">{value}</div>
      </div>
    </div>
  );
}

function ViewToggle({ view, onChange }) {
  return (
    <div className="adm-view-toggle">
      <button
        className={`adm-toggle-btn${view === "students" ? " adm-toggle-btn--active" : ""}`}
        onClick={() => onChange("students")}
      >
        <Users size={14} /> Student Insights
      </button>
      <button
        className={`adm-toggle-btn${view === "teachers" ? " adm-toggle-btn--active" : ""}`}
        onClick={() => onChange("teachers")}
      >
        <GraduationCap size={14} /> Teacher Insights
      </button>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <label className="adm-filter-select">
      <span className="adm-filter-label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function NoReportAvailable({ mode }) {
  const periodText =
    {
      all: "for any period",
      today: "for today",
      yesterday: "for yesterday",
      week: "for the last 7 days",
      month: "for the last 30 days",
      range: "for the selected date range",
    }[mode] || "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="adm-empty-state"
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#94a3b8"
        strokeWidth="1.2"
      >
        <path d="M3 3v18h18" />
        <path d="m7 16 4-4 4 4 4-4" />
      </svg>
      <div className="adm-empty-title">No report data</div>
      <div className="adm-empty-msg">
        There's no assignment activity {periodText} matching these filters. Try
        a different date range or clear a filter.
      </div>
    </motion.div>
  );
}

function PersonRow({ rank, name, meta, pct, isTop, isSelected, onClick }) {
  const badge = gradeBadge(pct);
  const barColor = isTop ? "#059669" : "#ef4444";
  return (
    <motion.div
      onClick={onClick}
      whileHover={{ x: 3 }}
      className="adm-person-row"
      style={{
        background: isSelected ? hex2rgba(barColor, 0.08) : "transparent",
        border: `1px solid ${isSelected ? hex2rgba(barColor, 0.3) : "transparent"}`,
      }}
    >
      <span
        className="adm-person-rank"
        style={{
          color:
            isTop && rank <= 3
              ? ["#f59e0b", "#94a3b8", "#b45309"][rank - 1]
              : "#94a3b8",
        }}
      >
        {isTop && rank <= 3 ? ["🥇", "🥈", "🥉"][rank - 1] : `#${rank}`}
      </span>
      <div className="adm-person-info">
        <span className="adm-person-name">{name}</span>
        {meta && <span className="adm-person-meta">{meta}</span>}
      </div>
      <div className="adm-person-bar-track">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="adm-person-bar-fill"
          style={{ background: barColor }}
        />
      </div>
      <span
        className="adm-person-badge"
        style={{ color: badge.color, background: hex2rgba(badge.color, 0.1) }}
      >
        {badge.label} {pct}%
      </span>
    </motion.div>
  );
}

function SortableTh({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`adm-th${active ? " adm-th--active" : ""}`}
    >
      {label}
      {active && (
        <ChevronDown
          size={12}
          style={{
            marginLeft: 4,
            transform: sort.dir === "asc" ? "rotate(180deg)" : "none",
          }}
        />
      )}
    </th>
  );
}

function DetailDrawer({ open, onClose, title, subtitle, children }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="adm-drawer-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="adm-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className="adm-drawer-header">
              <div>
                <h4>{title}</h4>
                {subtitle && <span className="adm-drawer-sub">{subtitle}</span>}
              </div>
              <button className="adm-close-btn" onClick={onClose}>
                <X size={20} />
              </button>
            </div>
            <div className="adm-drawer-body">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function StudentPicker({ students, disabled, selectedId, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset the local search text whenever the picker becomes disabled
  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setQuery("");
    }
  }, [disabled]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => s.name.toLowerCase().includes(q));
  }, [students, query]);

  const selectedStudent = students.find((s) => s.id === selectedId);

  return (
    <div
      ref={wrapperRef}
      className={`adm-student-picker${disabled ? " adm-student-picker--disabled" : ""}`}
    >
      <span className="adm-filter-label">Student</span>
      <div className="adm-student-picker-input-wrap">
        <Search size={14} className="adm-student-picker-icon" />
        <input
          type="text"
          disabled={disabled}
          placeholder={
            disabled
              ? "Select class & division first"
              : selectedStudent
                ? selectedStudent.name
                : "Search student…"
          }
          value={query}
          onFocus={() => !disabled && setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          className="adm-student-picker-input"
        />
        {selectedStudent && !open && (
          <button
            type="button"
            className="adm-student-picker-clear"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(null);
              setQuery("");
            }}
            aria-label="Clear selected student"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && !disabled && (
        <div className="adm-student-picker-dropdown">
          {filtered.length === 0 ? (
            <div className="adm-student-picker-empty">No students found</div>
          ) : (
            filtered.map((s) => (
              <div
                key={s.id}
                className={`adm-student-picker-option${
                  s.id === selectedId
                    ? " adm-student-picker-option--active"
                    : ""
                }`}
                onClick={() => {
                  onSelect(s.id);
                  setQuery("");
                  setOpen(false);
                }}
              >
                <span className="adm-student-picker-option-name">{s.name}</span>
                <span
                  className="adm-student-picker-option-pct"
                  style={{ color: gradeBadge(s.avgPct).color }}
                >
                  {s.avgPct}%
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminReportBody() {
  const nav = useNavigate();
  const dispatch = useDispatch();
  const [view, setView] = useState("students");
  const [mode, setMode] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [classFilter, setClassFilter] = useState("All");
  const [divisionFilter, setDivisionFilter] = useState("All");
  const [subjectFilter, setSubjectFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedClassSubject, setSelectedClassSubject] = useState(null);
  const [studentSort, setStudentSort] = useState({
    key: "avgPct",
    dir: "desc",
  });
  const [teacherSort, setTeacherSort] = useState({
    key: "avgScore",
    dir: "desc",
  });
  const [detailPanel, setDetailPanel] = useState(null); // { type: "student" | "teacher", id }
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);

  const [selectedTeacherClass, setSelectedTeacherClass] = useState(null);

  // Teacher Insights drill-down: Class & Division -> Subject -> Teacher -> Subject -> Chapter
  const [tiClass, setTiClass] = useState("");
  const [tiDivision, setTiDivision] = useState("");
  const [tiSubject, setTiSubject] = useState(null);
  const [tiTeacherId, setTiTeacherId] = useState(null);
  const [tiTeacherSubject, setTiTeacherSubject] = useState(null);

  const store = useSelector(s=>s);
  // NOTE: adjust this selector path to match your actual reducer slice for getAdminReport
  const adminReportData = useSelector((state) => state?.AdminPeopleManager?.adminReport);

  const assignments = useMemo(
    () => transformAdminReport(adminReportData),
    [adminReportData],
  );

  // Derive the subject list dynamically from real data instead of a hardcoded list
// Derive the subject list dynamically from real data instead of a hardcoded list
  const SUBJECTS = useMemo(() => {
    const set = new Set();
    assignments.forEach((a) => a.subject && set.add(a.subject));
    return Array.from(set).sort();
  }, [assignments]);

  // Derive classes dynamically from real data instead of a hardcoded list
  const CLASSES = useMemo(() => {
    const set = new Set();
    assignments.forEach((a) => a.class_name && set.add(a.class_name));
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [assignments]);

  // Derive divisions dynamically from real data instead of a hardcoded list
  const DIVISIONS = useMemo(() => {
    const set = new Set();
    assignments.forEach((a) => a.division && set.add(a.division));
    return Array.from(set).sort();
  }, [assignments]);

  useEffect(() => {
    dispatch(getAdminReport());
  }, []);

  useEffect(() => {
    const today = new Date();
    const iso = (d) => d.toISOString().split("T")[0];
    const off = (n) => {
      const d = new Date(today);
      d.setDate(today.getDate() + n);
      return iso(d);
    };
    if (mode === "today") setRange({ start: iso(today), end: iso(today) });
    else if (mode === "yesterday") setRange({ start: off(-1), end: off(-1) });
    else if (mode === "week") setRange({ start: off(-6), end: iso(today) });
    else if (mode === "month") setRange({ start: off(-29), end: iso(today) });
    else if (mode === "all") setRange({ start: "", end: "" });
    else if (mode === "range") setRange({ start: "", end: "" });
    setSelectedSubject(null);
    setSelectedClass(null);
    setSelectedClassSubject(null);
    setSelectedStudentId(null);
    setDetailPanel(null);
  }, [mode]);

  // Reset deeper drilldown levels whenever an upper level changes
  useEffect(() => {
    setTiSubject(null);
    setTiTeacherId(null);
    setTiTeacherSubject(null);
  }, [tiClass, tiDivision]);

  useEffect(() => {
    setTiTeacherId(null);
    setTiTeacherSubject(null);
  }, [tiSubject]);

  useEffect(() => {
    setTiTeacherSubject(null);
  }, [tiTeacherId]);

  const classOptions = useMemo(() => ["All", ...CLASSES], [CLASSES]);
  const divisionOptions = useMemo(() => ["All", ...DIVISIONS], [DIVISIONS]);
  const subjectOptions = useMemo(() => ["All", ...SUBJECTS], [SUBJECTS]);

  const filteredAssignments = useMemo(() => {
    const { start, end } = range;
    const q = search.trim().toLowerCase();
    return assignments.filter((a) => {
      if (classFilter !== "All" && a.class_name !== classFilter) return false;
      if (divisionFilter !== "All" && a.division !== divisionFilter)
        return false;
      if (subjectFilter !== "All" && a.subject !== subjectFilter) return false;
      const date = (a.assigned_date || "").split("T")[0];
      if (start && date < start) return false;
      if (end && date > end) return false;
      if (!q) return true;
      const haystack =
        `${a.subject} ${a.chapter} ${a.teacher_name} ${a.class_name}${a.division}`.toLowerCase();
      const studentMatch = a.student_details.some((s) =>
        (s.student_name || "").toLowerCase().includes(q),
      );
      return haystack.includes(q) || studentMatch;
    });
  }, [assignments, range, classFilter, divisionFilter, subjectFilter, search]);

  const kpis = useMemo(() => {
    const teacherIds = new Set();
    const studentIds = new Set();
    let totalAssigned = 0;
    let totalAttempted = 0;
    let pctSum = 0;
    let pctCount = 0;
    filteredAssignments.forEach((a) => {
      teacherIds.add(a.teacher_id);
      totalAssigned += a.no_of_students_assigned;
      totalAttempted += a.no_of_students_attempted;
      a.student_details.forEach((s) => {
        studentIds.add(s.student_id);
        if (s.attempted && s.percentage != null) {
          pctSum += s.percentage;
          pctCount++;
        }
      });
    });
    return {
      teachers: teacherIds.size,
      students: studentIds.size,
      assignments: filteredAssignments.length,
      avgScore: pctCount ? Math.round(pctSum / pctCount) : 0,
      attendance: totalAssigned
        ? Math.round((totalAttempted / totalAssigned) * 100)
        : 0,
    };
  }, [filteredAssignments]);

  /* ── Student aggregation ── */
  const studentMap = useMemo(() => {
    const map = {};
    filteredAssignments.forEach((a) => {
      a.student_details.forEach((s) => {
        // if (!s.attempted || s.percentage == null) return;
        const key = s.student_id;
        if (!map[key]) {
          map[key] = {
            id: key,
            name: s.student_name,
            class_name: a.class_name,
            division: a.division,
            totalPct: 0,
            count: 0,
            subjects: {},
          };
        }
        map[key].totalPct += s.percentage;
        map[key].count++;
        if (!map[key].subjects[a.subject]) {
          map[key].subjects[a.subject] = {
            subject: a.subject,
            totalPct: 0,
            count: 0,
            chapters: [],
          };
        }
        const subj = map[key].subjects[a.subject];
        subj.totalPct += s.percentage;
        subj.count++;
        subj.chapters.push({
          chapter: a.chapter,
          percentage: s.percentage,
          marks: s.marks_obtained,
          maxMarks: s.max_marks,
          date: (a.assigned_date || "").split("T")[0],
        });
      });
    });
    return map;
  }, [filteredAssignments]);

  const studentRoster = useMemo(
    () =>
      Object.values(studentMap).map((st) => ({
        id: st.id,
        name: st.name,
        class_name: st.class_name,
        division: st.division,
        attempts: st.count,
        avgPct: st.count ? Math.round(st.totalPct / st.count) : 0,
        subjectList: Object.values(st.subjects).map((sub) => ({
          subject: sub.subject,
          avgPct: Math.round(sub.totalPct / sub.count),
          chapters: [...sub.chapters].sort(
            (a, b) => a.percentage - b.percentage,
          ),
        })),
      })),
    [studentMap],
  );

  const sortedStudentRoster = useMemo(() => {
    const list = [...studentRoster];
    const { key, dir } = studentSort;
    list.sort((a, b) => {
      const va = a[key];
      const vb = b[key];
      if (typeof va === "string")
        return dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return dir === "asc" ? va - vb : vb - va;
    });
    return list;
  }, [studentRoster, studentSort]);

  const filteredStudentRoster = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedStudentRoster;
    return sortedStudentRoster.filter((s) => s.name.toLowerCase().includes(q));
  }, [sortedStudentRoster, search]);

  //---------------------- After selecting class and division show student list-----------------------------

  const studentPickerDisabled =
    classFilter === "All" || divisionFilter === "All";

  useEffect(() => {
    setSelectedStudentId(null);
  }, [classFilter, divisionFilter]);

  useEffect(() => {
    if (
      selectedStudentId &&
      !studentRoster.some((s) => s.id === selectedStudentId)
    ) {
      setSelectedStudentId(null);
    }
  }, [studentRoster, selectedStudentId]);

  function handleStudentSelect(id) {
    setSelectedStudentId(id);
    if (id) {
      setDetailPanel({ type: "student", id });
    } else {
      setDetailPanel(null);
    }
  }

  const subjectsAgg = useMemo(() => {
    const map = {};
    filteredAssignments.forEach((a) => {
      a.student_details.forEach((s) => {
        if (!s.attempted || s.percentage == null) return;
        if (!map[a.subject])
          map[a.subject] = { subject: a.subject, pctSum: 0, count: 0 };
        map[a.subject].pctSum += s.percentage;
        map[a.subject].count++;
      });
    });
    return Object.values(map).map((d) => ({
      subject: d.subject,
      score: d.count ? Math.round(d.pctSum / d.count) : 0,
      attempts: d.count,
    }));
  }, [filteredAssignments]);

  /* ── Class → Section drilldown aggregation ── */
  const classOnlyAgg = useMemo(() => {
    const map = {};
    CLASSES.forEach((cls) => {
      map[cls] = { studentIds: new Set(), pctSum: 0, pctCount: 0 };
    });
    filteredAssignments.forEach((a) => {
      const bucket = map[a.class_name];
      if (!bucket) return;
      a.student_details.forEach((s) => {
        bucket.studentIds.add(s.student_id);
        if (s.attempted && s.percentage != null) {
          bucket.pctSum += s.percentage;
          bucket.pctCount++;
        }
      });
    });
    return CLASSES.map((cls) => ({
      class_name: cls,
      students: map[cls].studentIds.size,
      score: map[cls].pctCount
        ? Math.round(map[cls].pctSum / map[cls].pctCount)
        : 0,
    }));
  }, [filteredAssignments]);

  const sectionAgg = useMemo(() => {
    if (!selectedClass) return [];
    const map = {};
    DIVISIONS.forEach((div) => {
      map[div] = { studentIds: new Set(), pctSum: 0, pctCount: 0 };
    });
    filteredAssignments.forEach((a) => {
      if (a.class_name !== selectedClass) return;
      const bucket = map[a.division];
      if (!bucket) return;
      a.student_details.forEach((s) => {
        bucket.studentIds.add(s.student_id);
        if (s.attempted && s.percentage != null) {
          bucket.pctSum += s.percentage;
          bucket.pctCount++;
        }
      });
    });
    return DIVISIONS.map((div) => ({
      division: div,
      strength: map[div].studentIds.size,
      score: map[div].pctCount
        ? Math.round(map[div].pctSum / map[div].pctCount)
        : 0,
    })).filter((d) => d.strength > 0);
  }, [filteredAssignments, selectedClass]);

  /* ── Subject-within-class drilldown, then section-within-subject drilldown ── */
  const classSubjectsAgg = useMemo(() => {
    if (!selectedClass) return [];
    const map = {};
    filteredAssignments.forEach((a) => {
      if (a.class_name !== selectedClass) return;
      if (!map[a.subject]) {
        map[a.subject] = {
          subject: a.subject,
          pctSum: 0,
          pctCount: 0,
          assignmentCount: 0,
        };
      }
      map[a.subject].assignmentCount++;
      a.student_details.forEach((s) => {
        if (!s.attempted || s.percentage == null) return;
        map[a.subject].pctSum += s.percentage;
        map[a.subject].pctCount++;
      });
    });
    return Object.values(map)
      .map((d) => ({
        subject: d.subject,
        score: d.pctCount ? Math.round(d.pctSum / d.pctCount) : 0,
        assignments: d.assignmentCount,
      }))
      .sort((a, b) => a.subject.localeCompare(b.subject));
  }, [filteredAssignments, selectedClass]);

const subjectChapterAgg = useMemo(() => {
    if (!selectedClass || !selectedClassSubject) return [];
    const map = {};
    filteredAssignments.forEach((a) => {
      if (a.class_name !== selectedClass || a.subject !== selectedClassSubject)
        return;
      if (!map[a.chapter]) {
        map[a.chapter] = {
          chapter: a.chapter,
          pctSum: 0,
          pctCount: 0,
          assignments: 0,
          totalAssigned: 0,
          totalAttempted: 0,
        };
      }
      const c = map[a.chapter];
      c.assignments++;
      c.totalAssigned += a.no_of_students_assigned;
      c.totalAttempted += a.no_of_students_attempted;
      a.student_details.forEach((s) => {
        if (!s.attempted || s.percentage == null) return;
        c.pctSum += s.percentage;
        c.pctCount++;
      });
    });
    return Object.values(map)
      .map((c) => ({
        chapter: c.chapter,
        score: c.pctCount ? Math.round(c.pctSum / c.pctCount) : 0,
        assignments: c.assignments,
        attendance: c.totalAssigned
          ? Math.round((c.totalAttempted / c.totalAssigned) * 100)
          : 0,
      }))
      .sort((a, b) => a.chapter.localeCompare(b.chapter));
  }, [filteredAssignments, selectedClass, selectedClassSubject]);

  /* ── Student tables scoped to the selected class ── */

  const rankedStudentRoster = useMemo(
    () => rankByEngagementAndScore(studentRoster),
    [studentRoster],
  );

const topStudents = useMemo(
  () => rankedStudentRoster.filter((s) => s.avgPct >= 60).slice(0, 8),
  [rankedStudentRoster],
);

  const weakStudents = useMemo(() => {
    const topIds = new Set(topStudents.map((s) => s.id));
    const remaining = rankedStudentRoster.filter((s) => !topIds.has(s.id));
    return [...remaining].reverse().slice(0, 8);
  }, [rankedStudentRoster, topStudents]);

  const topStudentIds = useMemo(
    () => new Set(topStudents.map((s) => s.id)),
    [topStudents],
  );
  const weakStudentIds = useMemo(
    () => new Set(weakStudents.map((s) => s.id)),
    [weakStudents],
  );

  const classStudentRoster = useMemo(() => {
    if (!selectedClass) return [];
    return studentRoster.filter((s) => s.class_name === selectedClass);
  }, [studentRoster, selectedClass]);

  const sectionScopedRoster = useMemo(() => {
    if (!selectedSection) return classStudentRoster;
    return classStudentRoster.filter((s) => s.division === selectedSection);
  }, [classStudentRoster, selectedSection]);

  const rankedSectionRoster = useMemo(
    () => rankByEngagementAndScore(sectionScopedRoster),
    [sectionScopedRoster],
  );

const classTopStudents = useMemo(
  () => rankedSectionRoster.filter((s) => s.avgPct >= 60).slice(0, 10),
  [rankedSectionRoster],
);

  const classWeakStudents = useMemo(() => {
    const topIds = new Set(classTopStudents.map((s) => s.id));
    const remaining = rankedSectionRoster.filter((s) => !topIds.has(s.id));
    return [...remaining].reverse().slice(0, 10);
  }, [rankedSectionRoster, classTopStudents]);

  const histogramData = useMemo(() => {
    const buckets = Array(10).fill(0);
    filteredAssignments.forEach((a) => {
      if (selectedSubject && a.subject !== selectedSubject) return;
      a.student_details.forEach((s) => {
        if (!s.attempted || s.percentage == null) return;
        buckets[Math.min(Math.floor(s.percentage / 10), 9)]++;
      });
    });
    return buckets;
  }, [filteredAssignments, selectedSubject]);

  /* ── Teacher aggregation ── */
  const teacherMap = useMemo(() => {
    const map = {};
    filteredAssignments.forEach((a) => {
      if (!map[a.teacher_id]) {
        map[a.teacher_id] = {
          id: a.teacher_id,
          name: a.teacher_name,
          subjects: new Set(),
          classes: new Set(),
          assignments: 0,
          totalAssigned: 0,
          totalAttempted: 0,
          pctSum: 0,
          pctCount: 0,
          list: [],
        };
      }
      const t = map[a.teacher_id];
      t.subjects.add(a.subject);
      t.classes.add(`${a.class_name}${a.division}`);
      t.assignments++;
      t.totalAssigned += a.no_of_students_assigned;
      t.totalAttempted += a.no_of_students_attempted;
      let chapterPctSum = 0;
      let chapterPctCount = 0;
      a.student_details.forEach((s) => {
        if (!s.attempted || s.percentage == null) return;
        t.pctSum += s.percentage;
        t.pctCount++;
        chapterPctSum += s.percentage;
        chapterPctCount++;
      });
      t.list.push({
        assignment_id: a.assignment_id,
        subject: a.subject,
        chapter: a.chapter,
        class_name: a.class_name,
        division: a.division,
        date: (a.assigned_date || "").split("T")[0],
        assigned: a.no_of_students_assigned,
        attempted: a.no_of_students_attempted,
        avgScore: chapterPctCount
          ? Math.round(chapterPctSum / chapterPctCount)
          : 0,
      });
    });
    return map;
  }, [filteredAssignments]);

  const teacherRoster = useMemo(
    () =>
      Object.values(teacherMap).map((t) => ({
        id: t.id,
        name: t.name,
        subjects: Array.from(t.subjects),
        classes: Array.from(t.classes),
        assignments: t.assignments,
        avgScore: t.pctCount ? Math.round(t.pctSum / t.pctCount) : 0,
        attendance: t.totalAssigned
          ? Math.round((t.totalAttempted / t.totalAssigned) * 100)
          : 0,
        list: [...t.list].sort((a, b) => b.date.localeCompare(a.date)),
      })),
    [teacherMap],
  );

  const classTeacherAgg = useMemo(() => {
    const map = {};
    CLASSES.forEach((cls) => {
      map[cls] = new Set();
    });
    filteredAssignments.forEach((a) => {
      if (map[a.class_name]) map[a.class_name].add(a.teacher_id);
    });
    return CLASSES.map((cls) => ({
      class_name: cls,
      teacherCount: map[cls].size,
    }));
  }, [filteredAssignments]);

  const classTeacherRoster = useMemo(() => {
    if (!selectedTeacherClass) return [];
    const map = {};
    filteredAssignments.forEach((a) => {
      if (a.class_name !== selectedTeacherClass) return;
      if (!map[a.teacher_id]) {
        map[a.teacher_id] = {
          id: a.teacher_id,
          name: a.teacher_name,
          subjects: new Set(),
          assignments: 0,
          totalAssigned: 0,
          totalAttempted: 0,
          pctSum: 0,
          pctCount: 0,
        };
      }
      const t = map[a.teacher_id];
      t.subjects.add(a.subject);
      t.assignments++;
      t.totalAssigned += a.no_of_students_assigned;
      t.totalAttempted += a.no_of_students_attempted;
      a.student_details.forEach((s) => {
        if (!s.attempted || s.percentage == null) return;
        t.pctSum += s.percentage;
        t.pctCount++;
      });
    });
    return Object.values(map)
      .map((t) => ({
        id: t.id,
        name: t.name,
        subjects: Array.from(t.subjects),
        assignments: t.assignments,
        avgScore: t.pctCount ? Math.round(t.pctSum / t.pctCount) : 0,
        attendance: t.totalAssigned
          ? Math.round((t.totalAttempted / t.totalAssigned) * 100)
          : 0,
      }))
      .sort((a, b) => b.avgScore - a.avgScore);
  }, [filteredAssignments, selectedTeacherClass]);

  const sortedTeacherRoster = useMemo(() => {
    const list = [...teacherRoster];
    const { key, dir } = teacherSort;
    list.sort((a, b) => {
      const va = a[key];
      const vb = b[key];
      if (typeof va === "string")
        return dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return dir === "asc" ? va - vb : vb - va;
    });
    return list;
  }, [teacherRoster, teacherSort]);

  const filteredTeacherRoster = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedTeacherRoster;
    return sortedTeacherRoster.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.subjects.some((s) => s.toLowerCase().includes(q)),
    );
  }, [sortedTeacherRoster, search]);

  const teacherLeaderboard = useMemo(
    () => rankTeachersByComposite(teacherRoster),
    [teacherRoster],
  );

  /* ── Teacher Insights drill-down aggregations ──
     Class + Division -> Subjects -> Teachers -> Subjects -> Chapters */
  const tiScopedAssignments = useMemo(() => {
    if (!tiClass || !tiDivision) return [];
    return filteredAssignments.filter(
      (a) => a.class_name === tiClass && a.division === tiDivision,
    );
  }, [filteredAssignments, tiClass, tiDivision]);

  const tiSubjectAgg = useMemo(() => {
    const map = {};
    tiScopedAssignments.forEach((a) => {
      if (!map[a.subject])
        map[a.subject] = {
          subject: a.subject,
          pctSum: 0,
          pctCount: 0,
          assignments: 0,
        };
      map[a.subject].assignments++;
      a.student_details.forEach((s) => {
        if (!s.attempted || s.percentage == null) return;
        map[a.subject].pctSum += s.percentage;
        map[a.subject].pctCount++;
      });
    });
    return Object.values(map)
      .map((d) => ({
        subject: d.subject,
        score: d.pctCount ? Math.round(d.pctSum / d.pctCount) : 0,
        assignments: d.assignments,
      }))
      .sort((a, b) => a.subject.localeCompare(b.subject));
  }, [tiScopedAssignments]);

  const tiTeacherAgg = useMemo(() => {
    if (!tiSubject) return [];
    const map = {};
    tiScopedAssignments.forEach((a) => {
      if (a.subject !== tiSubject) return;
      if (!map[a.teacher_id])
        map[a.teacher_id] = {
          id: a.teacher_id,
          name: a.teacher_name,
          pctSum: 0,
          pctCount: 0,
          assignments: 0,
          totalAssigned: 0,
          totalAttempted: 0,
        };
      const t = map[a.teacher_id];
      t.assignments++;
      t.totalAssigned += a.no_of_students_assigned;
      t.totalAttempted += a.no_of_students_attempted;
      a.student_details.forEach((s) => {
        if (!s.attempted || s.percentage == null) return;
        t.pctSum += s.percentage;
        t.pctCount++;
      });
    });
    return Object.values(map)
      .map((t) => ({
        id: t.id,
        name: t.name,
        score: t.pctCount ? Math.round(t.pctSum / t.pctCount) : 0,
        assignments: t.assignments,
        attendance: t.totalAssigned
          ? Math.round((t.totalAttempted / t.totalAssigned) * 100)
          : 0,
      }))
      .sort((a, b) => b.score - a.score);
  }, [tiScopedAssignments, tiSubject]);

  const tiTeacherSubjectAgg = useMemo(() => {
    if (!tiTeacherId) return [];
    const map = {};
    tiScopedAssignments.forEach((a) => {
      if (a.teacher_id !== tiTeacherId) return;
      if (!map[a.subject])
        map[a.subject] = {
          subject: a.subject,
          pctSum: 0,
          pctCount: 0,
          assignments: 0,
        };
      map[a.subject].assignments++;
      a.student_details.forEach((s) => {
        if (!s.attempted || s.percentage == null) return;
        map[a.subject].pctSum += s.percentage;
        map[a.subject].pctCount++;
      });
    });
    return Object.values(map)
      .map((d) => ({
        subject: d.subject,
        score: d.pctCount ? Math.round(d.pctSum / d.pctCount) : 0,
        assignments: d.assignments,
      }))
      .sort((a, b) => a.subject.localeCompare(b.subject));
  }, [tiScopedAssignments, tiTeacherId]);

  const tiChapterAgg = useMemo(() => {
    if (!tiTeacherId || !tiTeacherSubject) return [];
    const map = {};
    tiScopedAssignments.forEach((a) => {
      if (a.teacher_id !== tiTeacherId || a.subject !== tiTeacherSubject)
        return;
      if (!map[a.chapter])
        map[a.chapter] = {
          chapter: a.chapter,
          pctSum: 0,
          pctCount: 0,
          assignments: 0,
          totalAssigned: 0,
          totalAttempted: 0,
        };
      const c = map[a.chapter];
      c.assignments++;
      c.totalAssigned += a.no_of_students_assigned;
      c.totalAttempted += a.no_of_students_attempted;
      a.student_details.forEach((s) => {
        if (!s.attempted || s.percentage == null) return;
        c.pctSum += s.percentage;
        c.pctCount++;
      });
    });
    return Object.values(map)
      .map((c) => ({
        chapter: c.chapter,
        score: c.pctCount ? Math.round(c.pctSum / c.pctCount) : 0,
        assignments: c.assignments,
        attendance: c.totalAssigned
          ? Math.round((c.totalAttempted / c.totalAssigned) * 100)
          : 0,
      }))
      .sort((a, b) => a.chapter.localeCompare(b.chapter));
  }, [tiScopedAssignments, tiTeacherId, tiTeacherSubject]);

  const tiTeacherName = useMemo(() => {
    const t = tiTeacherAgg.find((t) => t.id === tiTeacherId);
    return t ? t.name : "";
  }, [tiTeacherAgg, tiTeacherId]);

  const detailStudent = useMemo(
    () =>
      detailPanel?.type === "student"
        ? studentRoster.find((s) => s.id === detailPanel.id)
        : null,
    [detailPanel, studentRoster],
  );
  const detailTeacher = useMemo(
    () =>
      detailPanel?.type === "teacher"
        ? teacherRoster.find((t) => t.id === detailPanel.id)
        : null,
    [detailPanel, teacherRoster],
  );

  const detailStudentCategory = useMemo(() => {
    if (!detailStudent) return null;
    if (topStudentIds.has(detailStudent.id)) return "top";
    if (weakStudentIds.has(detailStudent.id)) return "weak";
    // Fallback for students outside the top/bottom 8 — still uses the same blended signal
    const maxAttempts = Math.max(...studentRoster.map((s) => s.attempts), 1);
    const composite =
      (detailStudent.attempts / maxAttempts) * 0.5 +
      (detailStudent.avgPct / 100) * 0.5;
    return composite >= 0.5 ? "top" : "weak";
  }, [detailStudent, topStudentIds, weakStudentIds, studentRoster]);

  const CHIPS = [
    {
      icon: <GraduationCap size={18} />,
      label: "Total Teachers",
      value: adminReportData?.total_teachers || 0,
      color: "#6366f1",
    },
    {
      icon: <Users size={18} />,
      label: "Total Students",
      //   value: kpis.students,
      value: adminReportData?.total_students || 0,
      color: "#0891b2",
    },
    {
      icon: <BookOpen size={18} />,
      label: "Assignments",
      value: kpis.assignments,
      color: "#d97706",
    },
    {
      icon: <TrendingUp size={18} />,
      label: "Avg Score",
      value: `${kpis.avgScore}%`,
      color: "#059669",
    },
    {
      icon: <Award size={18} />,
      label: "OverAll Attendance",
      value: `${kpis.attendance}%`,
      color: "#7c3aed",
    },
  ];

  function resetAllFilters() {
    setMode("all");
    setRange({ start: "", end: "" });
    setClassFilter("All");
    setDivisionFilter("All");
    setSubjectFilter("All");
    setSearch("");
    setSelectedSubject(null);
    setSelectedClass(null);
    setSelectedSection(null);
    setSelectedClassSubject(null);
    setSelectedStudentId(null);
    setDetailPanel(null);
    setSelectedTeacherClass(null);
    setTiClass("");
    setTiDivision("");
    setTiSubject(null);
    setTiTeacherId(null);
    setTiTeacherSubject(null);
  }
  return (
    <div className="adm-wrapper">
      <div>
        <button className="dv-back-btn" onClick={() => nav(-1)}>
          <ChevronLeft size={18} /> Back
        </button>
      </div>
      <motion.div
        className="adm-page-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div>
          <h1 className="adm-page-title"> Report Board</h1>
          <p className="adm-page-subtitle">
            Unified performance insights across every teacher and student
          </p>
        </div>
      </motion.div>

      <motion.div
        className="adm-kpi-row"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        {CHIPS.map((c) => (
          <KpiChip
            key={c.label}
            icon={c.icon}
            label={c.label}
            value={c.value}
            color={c.color}
          />
        ))}
      </motion.div>

      <motion.div
        className="adm-controls"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <PeriodTabs mode={mode} onChange={setMode} />
        {mode === "range" && (
          <div className="adm-date-range">
            <input
              type="date"
              value={range.start}
              onChange={(e) =>
                setRange((p) => ({ ...p, start: e.target.value }))
              }
              className="adm-date-input"
            />
            <span className="adm-date-range-sep">—</span>
            <input
              type="date"
              value={range.end}
              onChange={(e) => setRange((p) => ({ ...p, end: e.target.value }))}
              className="adm-date-input"
            />
          </div>
        )}
        <ViewToggle view={view} onChange={setView} />
      </motion.div>
{
    view === "students" && <motion.div
        className="adm-filters"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.14 }}
      >
        <FilterSelect
          label="Class"
          value={classFilter}
          options={classOptions}
          onChange={setClassFilter}
        />
        <FilterSelect
          label="Division"
          value={divisionFilter}
          options={divisionOptions}
          onChange={setDivisionFilter}
        />
        {/* <FilterSelect
          label="Subject"
          value={subjectFilter}
          options={subjectOptions}
          onChange={setSubjectFilter}
        /> */}
        <StudentPicker
          students={studentRoster}
          disabled={studentPickerDisabled}
          selectedId={selectedStudentId}
          onSelect={handleStudentSelect}
        />
        <button
          type="button"
          className="adm-reset-btn"
          onClick={resetAllFilters}
          title="Reset all filters"
          aria-label="Reset all filters"
        >
          <RotateCcw size={16} />
        </button>
        {/* <div className="adm-search-box">
          <Search size={15} className="adm-search-icon" />
          <input
            placeholder="Search teacher, student or subject…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="adm-search-input"
          />
        </div> */}
      </motion.div>
}
      

      {filteredAssignments.length === 0 ? (
        <NoReportAvailable mode={mode} />
      ) : view === "students" ? (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
        >
          <div className="adm-grid" style={{ marginTop: 16 }}>
            <ChartCard
              title="Class-wise Overview (1–12)"
              subtitle="Click a class to view its section-wise breakdown"
              accent="#0891b2"
              delay={0.06}
            >
              {classOnlyAgg.every((d) => d.students === 0) ? (
                <NoData />
              ) : (
              <Bar
  data={{
    labels: classOnlyAgg.map((d) => `Class ${d.class_name}`),
    datasets: [
      {
        data: classOnlyAgg.map((d) => d.students),
        backgroundColor: classOnlyAgg.map((d) => {
          if (!selectedClass) return "#0891b2";
          return selectedClass === d.class_name
            ? "#0e7490"
            : hex2rgba("#0891b2", 0.22);
        }),
        hoverBackgroundColor: classOnlyAgg.map(() => "#0e7490"),
        borderRadius: 6,
        borderSkipped: false,
        barThickness: 20,
        minBarLength: 4,
      },
    ],
  }}
  options={mkOpts({
    yLabel: "",
    labelFn: (ctx) => {
      const r = classOnlyAgg[ctx.dataIndex];
      return [
        `Total Students: ${r.students}`,
        `Avg Score: ${r.score}%`,
      ];
    },
    clickFn: (_, els) => {
      if (!els.length) return;
      const cls = classOnlyAgg[els[0].index]?.class_name;
      setSelectedClass((p) => {
        const next = p === cls ? null : cls;
        setSelectedClassSubject(null);
        setSelectedSection(null); // reset section drilldown too
        return next;
      });
    },
  })}
/>
              )}
            </ChartCard>

            <ChartCard
              title={
                selectedClass
                  ? `Selected Class — Class ${selectedClass}${selectedSection ? `-${selectedSection}` : ""}`
                  : "All Sections"
              }
              subtitle={
                selectedClass
                  ? "click a row for details"
                  : "Select a class to view its student table"
              }
              accent="#7c3aed"
              delay={0.1}
            >
              {!selectedClass ? (
                <div className="adm-no-data">
                  <span
                    className="adm-no-data-text"
                    style={{ color: "#94a3b8" }}
                  >
                    Select a class to see section-wise data
                  </span>
                </div>
              ) : sectionAgg.length === 0 ? (
                <NoData />
              ) : (
                <Bar
                  data={{
                    labels: sectionAgg.map((d) => `Section ${d.division}`),
                    datasets: [
                      {
                        data: sectionAgg.map((d) => d.score),
                        backgroundColor: sectionAgg.map((d) =>
                          !selectedSection || selectedSection === d.division
                            ? "#7c3aed"
                            : hex2rgba("#7c3aed", 0.22),
                        ),
                        hoverBackgroundColor: sectionAgg.map(() => "#6d28d9"),
                        borderRadius: 6,
                        borderSkipped: false,
                        barThickness: 28,
                        minBarLength: 12,
                      },
                    ],
                  }}
                  options={mkOpts({
                    yLabel: "%",
                    yMax: 100,
                    labelFn: (ctx) => {
                      const r = sectionAgg[ctx.dataIndex];
                      return [
                        `Total Student: ${r.strength}`,
                        `Avg Score: ${r.score}%`,
                      ];
                    },
                    clickFn: (_, els) => {
                      if (!els.length) return;
                      const div = sectionAgg[els[0].index]?.division;
                      setSelectedSection((p) => (p === div ? null : div));
                    },
                  })}
                />
              )}
            </ChartCard>
          </div>

           <div className="adm-grid" style={{ marginTop: 16 }}>
            <ChartCard
              title={
                selectedClass
                  ? `Subjects — Class ${selectedClass}`
                  : "Subjects in Class"
              }
              subtitle={
                selectedClass
                  ? "Click a subject to see its section-wise breakdown"
                  : "Select a class above to view its subjects"
              }
              accent="#d97706"
              delay={0.14}
            >
              {!selectedClass ? (
                <div className="adm-no-data">
                  <span
                    className="adm-no-data-text"
                    style={{ color: "#94a3b8" }}
                  >
                    Select a class to see subject-wise data
                  </span>
                </div>
              ) : classSubjectsAgg.length === 0 ? (
                <NoData />
              ) : (
                <Bar
                  data={{
                    labels: classSubjectsAgg.map((d) =>
                      d.subject.length > 11
                        ? d.subject.slice(0, 11) + "…"
                        : d.subject,
                    ),
                    datasets: [
                      {
                        data: classSubjectsAgg.map((d) => d.score),
                        backgroundColor: classSubjectsAgg.map((d) => {
                          const c = pal(d.subject).solid;
                          return !selectedClassSubject ||
                            selectedClassSubject === d.subject
                            ? c
                            : hex2rgba(c, 0.22);
                        }),
                        hoverBackgroundColor: classSubjectsAgg.map(
                          (d) => pal(d.subject).solid,
                        ),
                        borderRadius: 6,
                        borderSkipped: false,
                        barThickness: 26,
                        minBarLength: 12,
                      },
                    ],
                  }}
                  options={mkOpts({
                    yLabel: "%",
                    yMax: 100,
                    labelFn: (ctx) => {
                      const r = classSubjectsAgg[ctx.dataIndex];
                      return [
                        `Avg Score: ${r.score}%`,
                        `Total Assignments: ${r.assignments}`,
                      ];
                    },
                    clickFn: (_, els) => {
                      if (!els.length) return;
                      const sub = classSubjectsAgg[els[0].index]?.subject;
                      setSelectedClassSubject((p) => (p === sub ? null : sub));
                    },
                  })}
                />
              )}
            </ChartCard>

          <ChartCard
              title={
                selectedClassSubject
                  ? `${selectedClassSubject} — Chapters`
                  : "Chapters for Subject"
              }
              subtitle={
                selectedClassSubject
                  ? `Class ${selectedClass} · avg score, assignments & attendance per chapter`
                  : "Click a subject on the left to view its chapters"
              }
              accent="#059669"
              delay={0.18}
            >
              {!selectedClass ? (
                <div className="adm-no-data">
                  <span
                    className="adm-no-data-text"
                    style={{ color: "#94a3b8" }}
                  >
                    Select a class, then a subject
                  </span>
                </div>
              ) : !selectedClassSubject ? (
                <div className="adm-no-data">
                  <span
                    className="adm-no-data-text"
                    style={{ color: "#94a3b8" }}
                  >
                    Select a subject to see chapter-wise data
                  </span>
                </div>
              ) : subjectChapterAgg.length === 0 ? (
                <NoData />
              ) : (
                <Bar
                  data={{
                    labels: subjectChapterAgg.map((d) =>
                      d.chapter.length > 14
                        ? d.chapter.slice(0, 14) + "…"
                        : d.chapter,
                    ),
                    datasets: [
                      {
                        data: subjectChapterAgg.map((d) => d.score),
                        backgroundColor: subjectChapterAgg.map(() => "#059669"),
                        hoverBackgroundColor: subjectChapterAgg.map(
                          () => "#047857",
                        ),
                        borderRadius: 6,
                        borderSkipped: false,
                        barThickness: 28,
                        minBarLength: 12,
                      },
                    ],
                  }}
                  options={mkOpts({
                    yLabel: "%",
                    yMax: 100,
                    labelFn: (ctx) => {
                      const r = subjectChapterAgg[ctx.dataIndex];
                      return [
                        `Avg Score: ${r.score}%`,
                        `Assignments: ${r.assignments}`,
                        `Attendance: ${r.attendance}%`,
                      ];
                    },
                  })}
                />
              )}
            </ChartCard>
          </div>

          <div className="adm-grid" style={{ marginTop: 16 }}>
            <ChartCard
              title={
                selectedClass
                  ? `Top Performers — Class ${selectedClass}`
                  : "Top Performers by Class"
              }
              subtitle={
                selectedClass
                  ? `${classTopStudents.length} student${classTopStudents.length !== 1 ? "s" : ""} · click a row for details`
                  : "Select a class above to view its student table"
              }
              accent="#059669"
              delay={0.3}
              height={280}
            >
              {!selectedClass ? (
                <div className="adm-no-data">
                  <span
                    className="adm-no-data-text"
                    style={{ color: "#94a3b8" }}
                  >
                    Select a class to see its students
                  </span>
                </div>
              ) : classTopStudents.length === 0 ? (
                <NoData />
              ) : (
                <div className="adm-table-wrap">
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Section</th>
                        <th>Attempts</th>
                        <th>Avg Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classTopStudents.map((s) => (
                        <tr
                          key={s.id}
                          className="adm-table-row"
                          onClick={() =>
                            setDetailPanel({ type: "student", id: s.id })
                          }
                        >
                          <td className="adm-table-name">{s.name}</td>
                          <td>{s.division}</td>
                          <td>{s.attempts}</td>
                          <td>
                            <span
                              className="adm-grade-badge"
                              style={{
                                color: gradeBadge(s.avgPct).color,
                                background: hex2rgba(
                                  gradeBadge(s.avgPct).color,
                                  0.1,
                                ),
                              }}
                            >
                              {s.avgPct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ChartCard>

            <ChartCard
              title={
                selectedClass
                  ? `Needs Attention — Class ${selectedClass}`
                  : "Needs Attention by Class"
              }
              subtitle={
                selectedClass
                  ? `${classWeakStudents.length} student${classWeakStudents.length !== 1 ? "s" : ""} · click a row for details`
                  : "Select a class above to view its student table"
              }
              accent="#ef4444"
              delay={0.34}
              height={280}
            >
              {!selectedClass ? (
                <div className="adm-no-data">
                  <span
                    className="adm-no-data-text"
                    style={{ color: "#94a3b8" }}
                  >
                    Select a class to see its students
                  </span>
                </div>
              ) : classWeakStudents.length === 0 ? (
                <NoData />
              ) : (
                <div className="adm-table-wrap">
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Section</th>
                        <th>Attempts</th>
                        <th>Avg Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classWeakStudents.map((s) => (
                        <tr
                          key={s.id}
                          className="adm-table-row"
                          onClick={() =>
                            setDetailPanel({ type: "student", id: s.id })
                          }
                        >
                          <td className="adm-table-name">{s.name}</td>
                          <td>{s.division}</td>
                          <td>{s.attempts}</td>
                          <td>
                            <span
                              className="adm-grade-badge"
                              style={{
                                color: gradeBadge(s.avgPct).color,
                                background: hex2rgba(
                                  gradeBadge(s.avgPct).color,
                                  0.1,
                                ),
                              }}
                            >
                              {s.avgPct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ChartCard>
          </div>

         
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
        >
          {/* ── Teacher Insights drilldown: Class/Division -> Subject -> Teacher -> Subject -> Chapter ── */}
          <motion.div
            className="adm-ti-drilldown"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
            style={{ marginTop: 24 }}
          >
            <div className="adm-filters" style={{ marginBottom: 4 }}>
              <label className="adm-filter-select">
                <span className="adm-filter-label">Class</span>
                <select
                  value={tiClass}
                  onChange={(e) => setTiClass(e.target.value)}
                >
                  <option value="">Select Class</option>
                  {CLASSES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label className="adm-filter-select">
                <span className="adm-filter-label">Division</span>
                <select
                  value={tiDivision}
                  onChange={(e) => setTiDivision(e.target.value)}
                >
                  <option value="">Select Division</option>
                  {DIVISIONS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
              {(tiClass || tiDivision) && (
                <button
                  type="button"
                  className="adm-reset-btn"
                  onClick={() => {
                    setTiClass("");
                    setTiDivision("");
                    setTiSubject(null);
                    setTiTeacherId(null);
                    setTiTeacherSubject(null);
                  }}
                  title="Reset drilldown"
                  aria-label="Reset drilldown"
                >
                  <RotateCcw size={16} />
                </button>
              )}
            </div>

            {tiClass && tiDivision && (
              <div
                className="adm-page-subtitle"
                style={{ marginBottom: 10, fontSize: 13 }}
              >
                Class {tiClass}-{tiDivision}
                {tiSubject ? ` › ${tiSubject}` : ""}
                {tiTeacherName ? ` › ${tiTeacherName}` : ""}
                {tiTeacherSubject ? ` › ${tiTeacherSubject}` : ""}
              </div>
            )}

            <div className="adm-grid">
              <ChartCard
                title="All Subjects"
                subtitle={
                  !tiClass || !tiDivision
                    ? "Select a class and division above"
                    : "Click a subject to see teachers"
                }
                accent="#d97706"
                delay={0.04}
              >
                {!tiClass || !tiDivision ? (
                  <div className="adm-no-data">
                    <span
                      className="adm-no-data-text"
                      style={{ color: "#94a3b8" }}
                    >
                      Select a class and division to view subjects
                    </span>
                  </div>
                ) : tiSubjectAgg.length === 0 ? (
                  <NoData />
                ) : (
                  <Bar
                    data={{
                      labels: tiSubjectAgg.map((d) =>
                        d.subject.length > 11
                          ? d.subject.slice(0, 11) + "…"
                          : d.subject,
                      ),
                      datasets: [
                        {
                          data: tiSubjectAgg.map((d) => d.score),
                          backgroundColor: tiSubjectAgg.map((d) => {
                            const c = pal(d.subject).solid;
                            return !tiSubject || tiSubject === d.subject
                              ? c
                              : hex2rgba(c, 0.22);
                          }),
                          hoverBackgroundColor: tiSubjectAgg.map(
                            (d) => pal(d.subject).solid,
                          ),
                          borderRadius: 6,
                          borderSkipped: false,
                          barThickness: 26,
                          minBarLength: 12,
                        },
                      ],
                    }}
                    options={mkOpts({
                      yLabel: "%",
                      yMax: 100,
                      labelFn: (ctx) => {
                        const r = tiSubjectAgg[ctx.dataIndex];
                        return [
                          `Avg Score: ${r.score}%`,
                          `Assignments: ${r.assignments}`,
                        ];
                      },
                      clickFn: (_, els) => {
                        if (!els.length) return;
                        const subj = tiSubjectAgg[els[0].index]?.subject;
                        setTiSubject((p) => (p === subj ? null : subj));
                      },
                    })}
                  />
                )}
              </ChartCard>

              <ChartCard
                title={tiSubject ? `Teachers — ${tiSubject}` : "Teachers"}
                subtitle={
                  !tiSubject
                    ? "Select a subject on the left"
                    : "Click a teacher to see the subjects they teach"
                }
                accent="#6366f1"
                delay={0.08}
              >
                {!tiSubject ? (
                  <div className="adm-no-data">
                    <span
                      className="adm-no-data-text"
                      style={{ color: "#94a3b8" }}
                    >
                      Select a subject to see its teachers
                    </span>
                  </div>
                ) : tiTeacherAgg.length === 0 ? (
                  <NoData />
                ) : (
                  <Bar
                    data={{
                      labels: tiTeacherAgg.map((t) =>
                        t.name.length > 14 ? t.name.slice(0, 14) + "…" : t.name,
                      ),
                      datasets: [
                        {
                          data: tiTeacherAgg.map((t) => t.score),
                          backgroundColor: tiTeacherAgg.map((t) =>
                            !tiTeacherId || tiTeacherId === t.id
                              ? "#6366f1"
                              : hex2rgba("#6366f1", 0.22),
                          ),
                          hoverBackgroundColor: tiTeacherAgg.map(
                            () => "#4f46e5",
                          ),
                          borderRadius: 6,
                          borderSkipped: false,
                          barThickness: 26,
                          minBarLength: 12,
                        },
                      ],
                    }}
                    options={mkOpts({
                      yLabel: "%",
                      yMax: 100,
                      labelFn: (ctx) => {
                        const t = tiTeacherAgg[ctx.dataIndex];
                        return [
                          `Avg Score: ${t.score}%`,
                          `Assignments: ${t.assignments}`,
                          `Attendance: ${t.attendance}%`,
                        ];
                      },
                      clickFn: (_, els) => {
                        if (!els.length) return;
                        const id = tiTeacherAgg[els[0].index]?.id;
                        setTiTeacherId((p) => (p === id ? null : id));
                      },
                    })}
                  />
                )}
              </ChartCard>
            </div>

            <div className="adm-grid" style={{ marginTop: 16 }}>
              <ChartCard
                title={
                  tiTeacherName
                    ? `Subjects Taught — ${tiTeacherName}`
                    : "Subjects Taught"
                }
                subtitle={
                  !tiTeacherId
                    ? "Select a teacher above"
                    : "Click a subject to see its chapters"
                }
                accent="#059669"
                delay={0.12}
              >
                {!tiTeacherId ? (
                  <div className="adm-no-data">
                    <span
                      className="adm-no-data-text"
                      style={{ color: "#94a3b8" }}
                    >
                      Select a teacher to see the subjects they teach
                    </span>
                  </div>
                ) : tiTeacherSubjectAgg.length === 0 ? (
                  <NoData />
                ) : (
                  <Bar
                    data={{
                      labels: tiTeacherSubjectAgg.map((d) =>
                        d.subject.length > 11
                          ? d.subject.slice(0, 11) + "…"
                          : d.subject,
                      ),
                      datasets: [
                        {
                          data: tiTeacherSubjectAgg.map((d) => d.score),
                          backgroundColor: tiTeacherSubjectAgg.map((d) => {
                            const c = pal(d.subject).solid;
                            return !tiTeacherSubject ||
                              tiTeacherSubject === d.subject
                              ? c
                              : hex2rgba(c, 0.22);
                          }),
                          hoverBackgroundColor: tiTeacherSubjectAgg.map(
                            (d) => pal(d.subject).solid,
                          ),
                          borderRadius: 6,
                          borderSkipped: false,
                          barThickness: 26,
                          minBarLength: 12,
                        },
                      ],
                    }}
                    options={mkOpts({
                      yLabel: "%",
                      yMax: 100,
                      labelFn: (ctx) => {
                        const r = tiTeacherSubjectAgg[ctx.dataIndex];
                        return [
                          `Avg Score: ${r.score}%`,
                          `Assignments: ${r.assignments}`,
                        ];
                      },
                      clickFn: (_, els) => {
                        if (!els.length) return;
                        const subj = tiTeacherSubjectAgg[els[0].index]?.subject;
                        setTiTeacherSubject((p) => (p === subj ? null : subj));
                      },
                    })}
                  />
                )}
              </ChartCard>

              <ChartCard
                title={
                //   tiTeacherSubject ? `Subject — ${tiTeacherSubject}` : "Chapters"
                  "All Chapters"
                }
                subtitle={
                  !tiTeacherSubject
                    ? "Select a subject on the left"
                    : "Hover a bar for assignments, avg score & attendance"
                }
                accent="#0891b2"
                delay={0.16}
              >
                {!tiTeacherSubject ? (
                  <div className="adm-no-data">
                    <span
                      className="adm-no-data-text"
                      style={{ color: "#94a3b8" }}
                    >
                      Select a subject to see its chapters
                    </span>
                  </div>
                ) : tiChapterAgg.length === 0 ? (
                  <NoData />
                ) : (
                  <Bar
                    data={{
                      labels: tiChapterAgg.map((c) =>
                        c.chapter.length > 14
                          ? c.chapter.slice(0, 14) + "…"
                          : c.chapter,
                      ),
                      datasets: [
                        {
                          data: tiChapterAgg.map((c) => c.score),
                          backgroundColor: tiChapterAgg.map(() => "#0891b2"),
                          hoverBackgroundColor: tiChapterAgg.map(
                            () => "#0e7490",
                          ),
                          borderRadius: 6,
                          borderSkipped: false,
                          barThickness: 26,
                          minBarLength: 12,
                        },
                      ],
                    }}
                    options={mkOpts({
                      yLabel: "%",
                      yMax: 100,
                      labelFn: (ctx) => {
                        const c = tiChapterAgg[ctx.dataIndex];
                        return [
                          `Avg Score: ${c.score}%`,
                          `Assignments: ${c.assignments}`,
                          `Attendance: ${c.attendance}%`,
                        ];
                      },
                    })}
                  />
                )}
              </ChartCard>
            </div>
          </motion.div>
        </motion.div>
      )}

      <DetailDrawer
        open={!!detailPanel}
        onClose={() => {
          setDetailPanel(null);
          setSelectedStudentId(null);
        }}
        title={
          detailStudent
            ? detailStudent.name
            : detailTeacher
              ? detailTeacher.name
              : ""
        }
        subtitle={
          detailStudent
            ? `Class ${detailStudent.class_name}${detailStudent.division}`
            : detailTeacher
              ? detailTeacher.subjects.join(", ")
              : ""
        }
      >
        {detailStudent && (                         //performance bar (top and need attention)
          <StudentDetailContent
            student={detailStudent}
            category={detailStudentCategory}
          />
        )}
        {/* {detailTeacher && <TeacherDetailContent teacher={detailTeacher} />} */}
      </DetailDrawer>
    </div>
  );
}